import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  BnbaCatalog,
  DtsenSet,
  bnbaErrorOf,
  useCreateBnbaDataset,
} from '@/hooks/use-bnba-datasets';
import { FieldPicker, SensitivityBadge } from './FieldPicker';
import { RowFilterEditor } from './RowFilterEditor';

const SET_LABEL: Record<DtsenSet, string> = {
  keluarga: 'Keluarga (satu baris per keluarga)',
  anggota: 'Anggota keluarga (satu baris per individu)',
};

function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 50);
}

interface BnbaDatasetBuilderProps {
  catalog: BnbaCatalog;
  onDone: () => void;
  onCancel: () => void;
}

export function BnbaDatasetBuilder({ catalog, onDone, onCancel }: BnbaDatasetBuilderProps) {
  const [name, setName] = useState('');
  const [tableName, setTableName] = useState('');
  const [tableNameTouched, setTableNameTouched] = useState(false);
  const [requester, setRequester] = useState('');
  const [approvalRef, setApprovalRef] = useState('');
  const [periode, setPeriode] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [sourceSet, setSourceSet] = useState<DtsenSet>('anggota');
  const [fields, setFields] = useState<string[]>([]);
  const [joinFields, setJoinFields] = useState<string[]>([]);
  const [filters, setFilters] = useState<Record<string, string[]>>({});
  const [joinFilters, setJoinFilters] = useState<Record<string, string[]>>({});
  const [piiConfirmed, setPiiConfirmed] = useState(false);
  const [problems, setProblems] = useState<string[]>([]);

  const createMutation = useCreateBnbaDataset();

  const partnerSet: DtsenSet = sourceSet === 'anggota' ? 'keluarga' : 'anggota';
  // Newest upload of each set; the catalog lists them newest first.
  const source = catalog.sources.find((s) => s.set === sourceSet);
  const partner = catalog.sources.find((s) => s.set === partnerSet);

  const ownFields = useMemo(
    () => catalog.fields.filter((f) => f.set === sourceSet),
    [catalog.fields, sourceSet]
  );
  const partnerFields = useMemo(
    () => catalog.fields.filter((f) => f.set === partnerSet),
    [catalog.fields, partnerSet]
  );

  const prefix = catalog.joinPrefix[sourceSet];
  const usesJoin = joinFields.length > 0 || Object.keys(joinFilters).length > 0;

  const selectedSensitive = [
    ...ownFields
      .filter((f) => fields.includes(f.name) && f.sensitivity !== 'none')
      .map((f) => ({ column: f.name, sensitivity: f.sensitivity })),
    ...partnerFields
      .filter((f) => joinFields.includes(f.name) && f.sensitivity !== 'none')
      .map((f) => ({ column: `${prefix}${f.name}`, sensitivity: f.sensitivity })),
  ];
  const selectedPii = selectedSensitive.filter((c) => c.sensitivity === 'pii');

  const emptyFilter = [...Object.values(filters), ...Object.values(joinFilters)].some(
    (v) => v.length === 0
  );

  const blockers: string[] = [];
  if (!source) blockers.push(`Belum ada unggahan set ${sourceSet}.`);
  else if (!source.buildable) blockers.push('Unggahan ini belum selesai ditransformasi.');
  if (usesJoin && !partner) blockers.push(`Belum ada unggahan set ${partnerSet} untuk digabung.`);
  else if (usesJoin && partner && !partner.buildable)
    blockers.push(`Unggahan set ${partnerSet} belum selesai ditransformasi.`);
  if (name.trim().length < 3) blockers.push('Nama dataset belum diisi.');
  if (!/^[a-z][a-z0-9_]{2,62}$/.test(tableName)) blockers.push('Nama tabel belum valid.');
  if (requester.trim().length < 2) blockers.push('Instansi pemohon belum diisi.');
  if (approvalRef.trim().length < 2) blockers.push('Nomor referensi persetujuan belum diisi.');
  if (!periode.trim()) blockers.push('Periode belum diisi.');
  if (fields.length === 0) blockers.push('Belum ada field yang dipilih.');
  if (emptyFilter) blockers.push('Ada pembatas baris yang belum punya nilai.');
  if (selectedPii.length > 0 && !piiConfirmed)
    blockers.push('Pernyataan field data pribadi belum dicentang.');

  const changeSet = (next: DtsenSet) => {
    setSourceSet(next);
    // Field names belong to a set; nothing carries over.
    setFields([]);
    setJoinFields([]);
    setFilters({});
    setJoinFilters({});
    setPiiConfirmed(false);
  };

  const submit = async () => {
    if (!source || blockers.length > 0) return;
    setProblems([]);
    try {
      await createMutation.mutateAsync({
        name: name.trim(),
        tableName,
        requester: requester.trim(),
        approvalRef: approvalRef.trim(),
        periode: periode.trim(),
        sourceSet,
        dataSourceId: source.dataSourceId,
        fields,
        filters,
        joinDataSourceId: usesJoin ? partner?.dataSourceId : undefined,
        joinFields,
        joinFilters,
        expiresAt: expiresAt ? new Date(`${expiresAt}T23:59:59+08:00`).toISOString() : undefined,
        piiConfirmed,
      });
      toast.success('Dataset tersimpan, build dimulai.');
      onDone();
    } catch (error) {
      const { message, problems: reasons } = bnbaErrorOf(error);
      setProblems(reasons.length ? reasons : [message]);
      toast.error(message);
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">1. Permohonan</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="bnba-name">Nama dataset</Label>
            <Input
              id="bnba-name"
              value={name}
              placeholder="BNBA Dinsos Tarakan — penerima PBI"
              onChange={(e) => {
                setName(e.target.value);
                if (!tableNameTouched) setTableName(`bnba_kustom_${slugify(e.target.value)}`);
              }}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="bnba-table">Nama tabel</Label>
            <Input
              id="bnba-table"
              value={tableName}
              onChange={(e) => {
                setTableNameTouched(true);
                setTableName(e.target.value);
              }}
            />
            <p className="text-xs text-muted-foreground">
              Huruf kecil, angka, dan garis bawah. Tampil di respons endpoint.
            </p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="bnba-requester">Instansi pemohon</Label>
            <Input
              id="bnba-requester"
              value={requester}
              onChange={(e) => setRequester(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="bnba-approval">Nomor referensi persetujuan</Label>
            <Input
              id="bnba-approval"
              value={approvalRef}
              onChange={(e) => setApprovalRef(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Dari aplikasi persetujuan. Dicatat di audit bersama daftar field.
            </p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="bnba-periode">Periode data</Label>
            <Input
              id="bnba-periode"
              value={periode}
              placeholder="2026 TW3"
              onChange={(e) => setPeriode(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="bnba-expires">Berlaku sampai (opsional)</Label>
            <Input
              id="bnba-expires"
              type="date"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">2. Sumber</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Select value={sourceSet} onValueChange={(v) => changeSet(v as DtsenSet)}>
            <SelectTrigger className="max-w-md">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="anggota">{SET_LABEL.anggota}</SelectItem>
              <SelectItem value="keluarga">{SET_LABEL.keluarga}</SelectItem>
            </SelectContent>
          </Select>
          {source ? (
            <p className="text-sm text-muted-foreground">
              {source.name} · {source.filename} · {source.rowCount.toLocaleString('id-ID')} baris ·
              diunggah {new Date(source.uploadedAt).toLocaleDateString('id-ID')}
            </p>
          ) : (
            <p className="text-sm text-destructive">Belum ada unggahan untuk set ini.</p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            3. Field yang disetujui{' '}
            <span className="font-normal text-muted-foreground">
              {fields.length + joinFields.length} dipilih
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <FieldPicker
            fields={ownFields}
            selected={fields}
            missing={source?.missingFields ?? []}
            onChange={setFields}
          />

          <div className="space-y-2">
            <p className="text-sm font-semibold">
              Field dari set {partnerSet} (digabung lewat {catalog.joinKey})
            </p>
            <p className="text-sm text-muted-foreground">
              {sourceSet === 'anggota'
                ? 'Tiap individu mendapat atribut keluarganya.'
                : 'Tiap keluarga mendapat atribut kepala keluarganya.'}{' '}
              Kolom tersaji dengan awalan <code>{prefix}</code>. Kunci gabung tidak ikut tersaji
              kecuali dicentang.
            </p>
            {partner ? (
              <FieldPicker
                fields={partnerFields}
                selected={joinFields}
                missing={partner.missingFields}
                prefix={prefix}
                onChange={setJoinFields}
              />
            ) : (
              <p className="text-sm text-destructive">
                Belum ada unggahan set {partnerSet}, jadi field-nya tidak bisa digabung.
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">4. Cakupan baris</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Tanpa pembatas, semua baris set ini ikut tersaji. Endpoint tidak menerima filter, jadi
            cakupan ditentukan di sini.
          </p>
          <RowFilterEditor
            fields={ownFields.filter(
              (f) => f.filterable && !(source?.missingFields ?? []).includes(f.name)
            )}
            value={filters}
            onChange={setFilters}
          />
          {partner ? (
            <div className="space-y-2">
              <p className="text-sm font-semibold">Berdasarkan set {partnerSet}</p>
              <p className="text-sm text-muted-foreground">
                Baris tanpa pasangan yang cocok tidak ikut tersaji.
              </p>
              <RowFilterEditor
                fields={partnerFields.filter(
                  (f) => f.filterable && !partner.missingFields.includes(f.name)
                )}
                value={joinFilters}
                onChange={setJoinFilters}
              />
            </div>
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">5. Ringkasan</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {selectedSensitive.length > 0 ? (
            <div className="space-y-2">
              <p className="text-sm font-semibold">Field pengenal yang akan tersaji</p>
              <div className="flex flex-wrap gap-2">
                {selectedSensitive.map((c) => (
                  <span key={c.column} className="inline-flex items-center gap-1.5">
                    <code className="text-xs">{c.column}</code>
                    <SensitivityBadge sensitivity={c.sensitivity} />
                  </span>
                ))}
              </div>
            </div>
          ) : null}

          {selectedPii.length > 0 ? (
            <label className="flex items-start gap-2 rounded-lg border border-destructive/40 p-3 text-sm">
              <Checkbox
                className="mt-0.5"
                checked={piiConfirmed}
                onCheckedChange={(v) => setPiiConfirmed(v === true)}
              />
              <span>
                Saya menyatakan {selectedPii.length} field data pribadi di atas tercantum dalam
                persetujuan {approvalRef.trim() || '(nomor belum diisi)'}.
              </span>
            </label>
          ) : null}

          <p className="text-sm text-muted-foreground">
            Endpoint dirilis gated: hanya terjangkau lewat SPLP. Baris disimpan terenkripsi.
          </p>

          {problems.length > 0 ? (
            <Alert variant="destructive">
              <AlertTitle>Permintaan tidak bisa dibangun</AlertTitle>
              <AlertDescription>
                <ul className="list-disc pl-4">
                  {problems.map((p) => (
                    <li key={p}>{p}</li>
                  ))}
                </ul>
              </AlertDescription>
            </Alert>
          ) : null}

          {blockers.length > 0 ? (
            <ul className="list-disc pl-4 text-sm text-muted-foreground">
              {blockers.map((b) => (
                <li key={b}>{b}</li>
              ))}
            </ul>
          ) : null}

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onCancel}>
              Batal
            </Button>
            <Button
              type="button"
              disabled={blockers.length > 0 || createMutation.isPending}
              onClick={submit}
            >
              {createMutation.isPending ? 'Menyimpan…' : 'Simpan dan build'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
